import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { CheckSquare, Square, Grid3x3, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { StatusDot } from "@/components/StatusDot";
import { MediaThumb } from "@/components/MediaThumb";
import { ClientCalendar } from "@/components/ClientCalendar";
import { MONTHS } from "@/lib/constants";
import { todayMonth } from "@/lib/format";

export default function ClientPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sharedMonth = searchParams.get("m");
  const [ctx, setCtx] = useState(null);
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("mes");
  const [error, setError] = useState("");
  const [batch, setBatch] = useState([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => { load(); }, [token]);
  const load = async () => {
    try {
      const home = await api.get(`/c/${token}`);
      setCtx(home.data);
      document.documentElement.style.setProperty("--brand-color", home.data.brand?.color || "#4A5568");
      const p = await api.get(`/c/${token}/posts`);
      setPosts(p.data);
      const mo = p.data[0]?.date?.slice(0, 7) || todayMonth();
      const mn = Number(mo.split("-")[1]);
      document.title = `Calendario del mes de ${MONTHS[mn - 1]} de ${home.data.brand?.name}`;
    } catch (e) {
      setError(e.response?.data?.detail || "Este enlace ya no es válido.");
    }
  };

  const reviewable = posts.filter((p) => p.status === "enviado_cliente");
  const toggleBatch = (id) => setBatch((b) => b.includes(id) ? b.filter((x) => x !== id) : [...b, id]);
  const doBatch = async () => {
    await api.post(`/c/${token}/batch-approve`, { post_ids: batch });
    toast.success(`Aprobados ${batch.length}`);
    setBatch([]); setConfirming(false); load();
  };
  const thumbOf = (p) => p.cover_url || (p.files?.[0] && (p.files[0].type === "video" ? p.files[0].thumbnail : p.files[0].url));

  if (error) return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6 text-center">
      <div><p className="font-display text-3xl mb-2">Enlace no válido</p><p className="text-subtle text-sm">{error}</p></div>
    </div>
  );
  if (!ctx) return <div className="min-h-screen bg-surface flex items-center justify-center text-subtle">Cargando…</div>;

  const brand = ctx.brand;

  return (
    <div className="min-h-screen bg-surface pb-28">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-30 bg-surface/95 backdrop-blur border-b border-line">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 h-16 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full overflow-hidden border border-line bg-app flex items-center justify-center shrink-0">
            {brand?.logo_url ? <img src={fileUrl(brand.logo_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-display" style={{ color: brand?.color }}>{brand?.name?.[0]}</span>}
          </div>
          <span className="font-medium text-sm">{brand?.name}</span>
          {ctx.pending > 0 && (
            <span className="ml-auto text-xs font-data px-3 py-1.5 rounded-full text-white" style={{ background: "var(--brand-color)" }} data-testid="pending-count">
              {ctx.pending} por revisar
            </span>
          )}
        </div>
      </header>

      <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 pt-8">
        <h1 className="font-display text-4xl mb-1">Hola, {ctx.member.name.split(" ")[0]}</h1>
        <p className="text-subtle text-sm mb-7">Revisa el contenido y aprueba lo que te encaje.</p>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex border border-line rounded-full overflow-hidden p-0.5 bg-app">
            <button onClick={() => setView("mes")} data-testid="client-view-mes" className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-colors ${view === "mes" ? "bg-surface text-ink shadow-sm" : "text-subtle"}`}><Grid3x3 size={14} /> Mes</button>
            <button onClick={() => setView("feed")} data-testid="client-view-feed" className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-colors ${view === "feed" ? "bg-surface text-ink shadow-sm" : "text-subtle"}`}><LayoutGrid size={14} /> Feed</button>
          </div>
          {reviewable.length > 0 && (
            <button onClick={() => setBatch(reviewable.map((p) => p.id))} data-testid="select-all-review" className="ml-auto text-xs text-subtle underline">Seleccionar los {reviewable.length} por revisar</button>
          )}
        </div>

        {posts.length === 0 ? (
          <div className="border border-line bg-app rounded-3xl py-20 text-center">
            <p className="font-display text-2xl mb-2">Nada por ahora</p>
            <p className="text-subtle text-sm">Cuando tu estudio envíe contenido, aparecerá aquí.</p>
          </div>
        ) : view === "mes" ? (
          <ClientCalendar posts={posts} initialMonth={sharedMonth || posts.map((p) => p.date.slice(0, 7)).find((m) => m >= todayMonth()) || todayMonth()} onSelect={(id) => navigate(`/c/${token}/post/${id}`)} />
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="client-feed">
            {posts.map((p) => {
              return (
                <button key={p.id} onClick={() => navigate(`/c/${token}/post/${p.id}`)} data-testid={`client-post-${p.id}`} className="aspect-square bg-app relative rounded-2xl overflow-hidden hover:opacity-90 transition-opacity">
                  <MediaThumb post={p} />
                  {p.status === "enviado_cliente" && <span className="absolute top-2 right-2"><StatusDot color="#16161A" size={9} /></span>}
                </button>
              );
            })}
          </motion.div>
        )}

        {/* Batch approve list */}
        {reviewable.length > 0 && (
          <div className="mt-10 max-w-2xl">
            <h2 className="font-display text-lg mb-3">Por revisar</h2>
            <div className="space-y-2">
              {reviewable.map((p) => {
                const t = thumbOf(p);
                const sel = batch.includes(p.id);
                return (
                  <div key={p.id} className="flex items-center gap-3 border border-line bg-surface p-2 rounded-2xl">
                    <button onClick={() => toggleBatch(p.id)} data-testid={`batch-toggle-${p.id}`} className="pl-1">{sel ? <CheckSquare size={20} style={{ color: "var(--brand-color)" }} /> : <Square size={20} className="text-subtle" />}</button>
                    <button onClick={() => navigate(`/c/${token}/post/${p.id}`)} className="flex items-center gap-3 flex-1 text-left min-w-0">
                      <div className="w-12 h-12 bg-app rounded-xl overflow-hidden shrink-0">{t && <img src={fileUrl(t)} alt="" className="w-full h-full object-cover" />}</div>
                      <span className="text-sm truncate">{p.text ? p.text.slice(0, 50) : "Sin texto"}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {batch.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-surface border-t border-line p-4 z-40">
          <div className="mx-auto w-full max-w-[1400px] px-2 sm:px-8 flex items-center gap-3">
            <span className="text-sm">{batch.length} seleccionados</span>
            <button onClick={() => setConfirming(true)} data-testid="batch-approve-btn" className="ml-auto px-6 py-3 text-white text-sm rounded-full" style={{ background: "var(--brand-color)" }}>Aprobar seleccionados</button>
          </div>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-ink/30 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setConfirming(false)}>
          <div className="bg-surface border border-line rounded-3xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="batch-confirm">
            <h2 className="font-display text-2xl mb-3">Aprobar {batch.length} posts</h2>
            <ul className="text-sm text-subtle space-y-1 mb-5 max-h-40 overflow-y-auto">
              {batch.map((id) => { const p = posts.find((x) => x.id === id); return <li key={id} className="truncate">· {p?.text?.slice(0, 40) || "Sin texto"}</li>; })}
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 py-3 border border-line text-sm rounded-full">Cancelar</button>
              <button onClick={doBatch} data-testid="confirm-batch" className="flex-1 py-3 text-white text-sm rounded-full" style={{ background: "var(--brand-color)" }}>Aprobar</button>
            </div>
          </div>
        </div>
      )}

      <footer className="flex flex-col items-center gap-1.5 py-8 mt-8">
        <span className="text-[10px] text-subtle">Un calendario de</span>
        <img src="/studio83-logo.png" alt="Studio 83" className="h-3 w-auto opacity-40" style={{ filter: "brightness(0)" }} />
      </footer>
    </div>
  );
}
